import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AUTH_EXPIRED_EVENT,
  apiFetch,
  getAdminToken,
  readApiBody,
  setAdminToken,
} from "../lib/api";

type AuthState = {
  token: string | null;
  email: string | null;
  loading: boolean;
};

type AuthContextValue = AuthState & {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(() => getAdminToken());
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onExpired = () => {
      setToken(null);
      setEmail(null);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const login = useCallback(async (e: string, password: string) => {
    setLoading(true);
    try {
      const res = await apiFetch("/api/auth/login", {
        method: "POST",
        json: { email: e, password },
      });
      const data = await readApiBody<{ token: string; email?: string }>(
        res,
        "No se pudo iniciar sesión"
      );
      setAdminToken(data.token);
      setToken(data.token);
      setEmail(data.email || e);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setAdminToken(null);
    setToken(null);
    setEmail(null);
  }, []);

  const value = useMemo(
    () => ({ token, email, loading, login, logout }),
    [token, email, loading, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
}
